/* eslint-disable react/no-array-index-key, no-eval */
import React, { PureComponent } from "react";
import { connect } from "react-redux";
import { I18n } from "react-redux-i18n";
import Components from "@alboom/custom-page-components/components";
import { hasPermission as cerberusHasPermission } from "@alboom/alboom-cerberus";
import EditorPanel from "editor/components/EditorPanel";
import { v4 as uuid } from "uuid";
import Icon from "@alboompro/boom-components/general/icon";
import Tool from "./Tool";

import {
  ToolsBarContainer,
  ToolsTitle,
  ToolsContent,
  ToolsIconsContainer,
  SearchInput,
  NoResultsContainer,
} from "./styles";

const propsReducer = (props, helpers = {}, locale) => {
  // console.log(props)
  // console.log(locale)
  return props.reduce((curr, prop) => {
    if (prop.group) {
      const groupProps = propsReducer(prop.props, helpers);
      return {
        ...curr,
        ...groupProps,
      };
    }

    // if our prop is fromStore, we need to maker further processing
    if (prop.fromStore) {
      return {
        ...curr,
        [prop.name]: {
          needComputing: true,
          fn: helpers[prop.function],
        },
      };
    }
    // Here in some cases we need to check from where to get our default
    // props, so for some custom elements, custom logic is needed.
    switch (prop.element) {
      // If our element is RangeSlide, we need to retrieve the default value
      // from the default option of the slider.
      case "RangeSlide": {
        const defaultType =
          prop.options.filter((item) => item.type === prop.defaultType)[0] ||
          prop.options[0];

        const props = {
          [prop.name]: defaultType.defaultValue,
          [`${prop.name}Type`]: defaultType.type,
        };

        return {
          ...curr,
          ...props,
        };
      }
      case "Input": {
        const inputFilterList = {
          countdownTextForDays: I18n.t("editPanel.countdown.textForDays"),
          countdownTextForHours: I18n.t("editPanel.countdown.textForHours"),
          countdownTextForMinutes: I18n.t("editPanel.countdown.textForMinutes"),
          countdownTextForSeconds: I18n.t("editPanel.countdown.textForSeconds"),
        };

        const isPropInBlackList = Object.keys(inputFilterList).find(
          (item) => item === prop.name
        );

        if (isPropInBlackList) {
          return {
            ...curr,
            [prop.name]: inputFilterList[prop.name],
          };
        }

        return {
          ...curr,
          [prop.name]: prop.defaultValue,
        };
      }
      case "DateTimerPicker": {
        const currentDate = new Date();
        return {
          ...curr,
          [prop.name]: new Date(
            currentDate.getTime() + 7 * 24 * 60 * 60 * 1000
          ).toISOString(),
        };
      }
      case "FAQManager": {
        const initialQuestionsAmount = 2;
        const defaultFaqArray = [];

        for (let i = 1; i <= initialQuestionsAmount; i += 1) {
          defaultFaqArray.push({
            id: uuid(),
            questionText: `${
              I18n.t("editPanel.FAQManager.faqPlaceholder.questionText") + i
            }`,
            questionInnerHTML: `<p>${
              I18n.t("editPanel.FAQManager.faqPlaceholder.questionInnerHTML") +
              i
            }</p>`,
            answerText: `${
              I18n.t("editPanel.FAQManager.faqPlaceholder.answerText") + i
            }`,
            answerInnerHTML: `<p>${
              I18n.t("editPanel.FAQManager.faqPlaceholder.answerInnerHTML") + i
            }</p>`,
          });
        }

        return {
          ...curr,
          [prop.name]: defaultFaqArray,
        };
      }
      case "WhatsappMessage": {
        return {
          ...curr,
          [prop.name]: I18n.t("editPanel.textarea.defaultMessage"),
        };
      }
      default:
        return {
          ...curr,
          [prop.name]: prop.defaultValue,
        };
    }
  }, {});
};

/**
 * this will read the following properties from manifest: countriesAllowed
 * and countriesDenied and will return true if the country is allowed
 * or false if it's denied
 *
 * It'll use the iso and iso3 properties of the country object to check
 * if the country is allowed or denied.
 *
 * @param {JSON} manifest Component manifest
 * @param {JSON} country Country data
 * @returns {Boolean} True if country is allowed
 */
const isCountryAllowed = (manifest, country) => {
  const { iso, iso3 } = country;
  if (!manifest.countriesAllowed && !manifest.countriesDenied) {
    return true;
  }

  const isAllowed =
    (manifest.countriesAllowed && manifest.countriesAllowed.includes(iso)) ||
    (manifest.countriesAllowed && manifest.countriesAllowed.includes(iso3));
  const isDenied =
    (manifest.countriesDenied && manifest.countriesDenied.includes(iso)) ||
    (manifest.countriesDenied && manifest.countriesDenied.includes(iso3));

  return isAllowed || !isDenied;
};

/**
 * This will get all components registered in components from
 * @alboom/custom-page-components and prepare an array of objects
 * which will be used in order to display our elements menu, and also
 * populate it's properties so when an element gets dragged into the page,
 * it'll inherit the correct default properties.
 *
 * @return {Array} properties mapped
 */
const generateElementArray = (locale, country, betas, hasPermission) => {
  return Object.keys(Components)
    .map((key) => {
      const { manifest, helpers } = Components[key].default;

      // TODO: remove this when we have a better way to handle beta elements
      // and control which elements are available for which countries
      // For the time being this takes a waterfall approach, so if the element
      // is not allowed for the country, it won't be allowed even if it's a beta
      // element.
      //
      // Other conditions must be placed according to their priority, so if
      // if a condition has a higher priority, it must be placed before other
      // conditions.
      if (
        !manifest.disabled &&
        isCountryAllowed(manifest, country) &&
        (!manifest.beta || betas.includes("page_editor_testers"))
      ) {
        const defaultProps = propsReducer(manifest.props, helpers, locale);

        return {
          icon: manifest.icon,
          tags: manifest.tags,
          menu: manifest.menu,
          title: manifest.name[locale],
          component: key,
          version: manifest.version,
          props: defaultProps,
          beta: manifest.beta,
          newElement: manifest.new,
          editMode: manifest.editMode,
          cerberus: manifest.cerberus,
          hasPermissionFunc: manifest.cerberus && hasPermission,
          disabled: eval(manifest.disabled),
          disabledText: manifest.disabledText && manifest.disabledText[locale],
        };
      }
      return null;
    })
    .filter((i) => i !== null)
    .reduce((curr, item) => {
      return {
        ...curr,
        [item.menu]: [...(curr[item.menu] || []), item],
      };
    }, {});
};

class ToolsIcons extends PureComponent {
  state = {
    components: [],
    searchValue: "",
  };

  componentWillMount() {
    const { locale, betas, hasPermissionFunc, country } = this.props;
    this.setState({
      components: generateElementArray(
        locale,
        country,
        betas,
        hasPermissionFunc
      ),
    });
  }

  generateElementsArray = () => {
    const { components, searchValue } = this.state;

    if (searchValue) {
      const filteredComponents = Object.keys(components).reduce((curr, key) => {
        const filteredElements = components[key].filter((element) => {
          if (element.tags) {
            return element.tags.some((tag) =>
              tag.toLowerCase().includes(searchValue.toLowerCase())
            );
          }
          return element.title
            .toLowerCase()
            .includes(searchValue.toLowerCase());
        });

        return {
          ...curr,
          [key]: filteredElements,
        };
      }, {});

      return [
        {
          menu: "filtered",
          elements: Object.keys(filteredComponents).reduce(
            (acc, key) => [...acc, ...filteredComponents[key]],
            []
          ),
        },
      ];
    }

    const menusArray = Object.keys(components).map((key) => {
      return {
        menu: key,
        elements: components[key],
      };
    });
    return menusArray;
  };

  render() {
    const { searchValue } = this.state;
    const { selected, editMode } = this.props;

    const elementsArray = this.generateElementsArray();

    return (
      <ToolsIconsContainer elementSelected={selected && editMode}>
        <SearchInput
          placeholder={I18n.t("general.searchElement")}
          onChange={(e) => this.setState({ searchValue: e.target.value })}
          suffix={
            <Icon
              kind="bold"
              group="interface-essential"
              category="search"
              file="search-1.svg"
              size="12"
              color="#A4A4A4"
            />
          }
        />
        <ToolsIconsContainer elementSelected={selected && editMode}>
          {elementsArray.map((group, index) => {
            return group.elements.length > 0 ? (
              <div className="tool-group" key={group.menu}>
                {index !== 0 && !searchValue && (
                  <ToolsTitle>
                    {I18n.t(`general.menu.${group.menu}`)}
                  </ToolsTitle>
                )}
                <ToolsContent>
                  {group.elements.map((element, i) => {
                    return (
                      <Tool key={`cmp-${element.title}${i}`} {...element} />
                    );
                  })}
                </ToolsContent>
              </div>
            ) : (
              <NoResultsContainer>
                <Icon
                  kind="bold"
                  group="interface-essential"
                  category="search"
                  file="search-1.svg"
                  size="30"
                  color="#969696"
                />
                {I18n.t("general.noResults")}
              </NoResultsContainer>
            );
          })}
        </ToolsIconsContainer>
      </ToolsIconsContainer>
    );
  }
}

const ToolsBar = ({
  selected,
  editMode,
  locale,
  betas,
  country,
  cerberusHasPermission,
}) => {
  if (!betas) return null;
  return (
    <ToolsBarContainer>
      <ToolsIcons
        selected={selected}
        editMode={editMode}
        locale={locale}
        betas={betas}
        country={country}
        hasPermissionFunc={cerberusHasPermission}
      />
      <EditorPanel />
    </ToolsBarContainer>
  );
};

const mapStateToProps = (state) => {
  return {
    betas: state.user && state.user.beta_testers,
    country: state.user && state.user.country,
    locale: state.i18n.locale,
    selected: state.editor.selected,
    editMode: state.editor.editMode,
  };
};

const mapDispatchToProps = {
  cerberusHasPermission,
};

export default connect(mapStateToProps, mapDispatchToProps)(ToolsBar);
